/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, Network, BarChart3, GitCompareArrows, Eye, Layers,
  ArrowRight, Crown,
} from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

/**
 * ConvMultiAgenceProvidence — Hero dédié au pilotage multi-agence.
 * Inséré APRÈS ConvCopilots (3 IA) pour s'adresser aux dirigeants de réseau.
 * DA Providence : violet/pourpre dense, monospace techno, mouvement subtil.
 * Objectif : CTA démo orienté groupes.
 */
export const ConvMultiAgenceProvidence = ({ onDemoClick }) => {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden" data-testid="multi-agence-providence-section">
      {/* Background : gradient + grain */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0414] via-[#050308] to-[#0a0414]" />
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#A855F7]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#FF914D]/8 rounded-full blur-[100px]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 md:px-12">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#A855F7]/40 bg-[#A855F7]/10 text-[11px] font-medium tracking-widest text-[#C084FC] uppercase" style={MONO}>
            <Sparkles className="w-3 h-3" />
            Nouveau · Pour les groupes & réseaux
          </span>

          <h2 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white max-w-4xl mx-auto leading-[1.05]" style={DISPLAY}>
            Pilotez votre réseau d'agences{' '}
            <span className="bg-gradient-to-r from-[#A855F7] via-[#C084FC] to-[#FF914D] bg-clip-text text-transparent">en un seul écran.</span>
          </h2>

          <p className="mt-6 text-base sm:text-lg text-white/65 max-w-2xl mx-auto leading-relaxed">
            Providence est l'IA stratégique du dirigeant multi-agence. Elle compare vos agences,
            détecte les signaux faibles et vous dit <span className="text-white font-medium">qui appeler cette semaine.</span>{' '}
            Sans micro-management. Sans court-circuiter vos directeurs.
          </p>
        </motion.div>

        {/* Mockup central — Dashboard Groupe */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-14 relative"
          data-testid="providence-mockup"
        >
          <div className="relative rounded-2xl border border-[#A855F7]/20 bg-[#0c0814]/80 backdrop-blur shadow-2xl shadow-[#A855F7]/10 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-black/30">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#10b981] to-[#0d9668] flex items-center justify-center">
                  <BarChart3 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-sm text-white font-medium">Groupe SLV Immobilier</div>
                  <div className="text-[10px] text-white/40" style={MONO}>14 AGENCES · SCORE 82/100 · +6% CE MOIS</div>
                </div>
              </div>
              <div className="flex gap-2 text-[10px] text-white/40" style={MONO}>
                <span className="px-2 py-1 rounded bg-white/5">VUE GROUPE</span>
                <span className="px-2 py-1 rounded bg-[#A855F7]/15 text-[#C084FC]">PROVIDENCE</span>
              </div>
            </div>

            {/* Body : grid layout (KPI + Providence chat) */}
            <div className="p-6 grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Left : KPI + comparatif */}
              <div className="lg:col-span-3 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                    <div className="text-[9px] uppercase text-white/40 tracking-widest mb-1" style={MONO}>Score groupe</div>
                    <div className="text-2xl font-bold text-white" style={DISPLAY}>82<span className="text-xs text-white/40">/100</span></div>
                    <div className="text-[10px] text-[#10b981] mt-1" style={MONO}>↑ +6% vs mois N-1</div>
                  </div>
                  <div className="p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                    <div className="text-[9px] uppercase text-white/40 tracking-widest mb-1" style={MONO}>Performantes</div>
                    <div className="text-2xl font-bold text-[#10b981]" style={DISPLAY}>9<span className="text-xs text-white/40">/14</span></div>
                    <div className="text-[10px] text-white/40 mt-1" style={MONO}>agences ≥ 80/100</div>
                  </div>
                  <div className="p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                    <div className="text-[9px] uppercase text-white/40 tracking-widest mb-1" style={MONO}>À accompagner</div>
                    <div className="text-2xl font-bold text-[#dc2626]" style={DISPLAY}>2</div>
                    <div className="text-[10px] text-white/40 mt-1" style={MONO}>agences en risque</div>
                  </div>
                </div>

                <div className="rounded-lg border border-white/5 bg-white/[0.02] overflow-hidden">
                  <div className="px-3 py-2 text-[9px] uppercase text-white/40 tracking-widest border-b border-white/5 flex justify-between" style={MONO}>
                    <span>Comparatif agences</span>
                    <span className="text-white/30">Score · Évol. 30j</span>
                  </div>
                  {[
                    { name: 'Avignon', score: 91, trend: '+8%', color: '#10b981' },
                    { name: 'Marseille', score: 85, trend: '+3%', color: '#10b981' },
                    { name: 'Carpentras', score: 67, trend: '-11%', color: '#d97706' },
                    { name: 'Orange', score: 54, trend: '-17%', color: '#dc2626' },
                  ].map((a) => (
                    <div key={a.name} className="flex items-center justify-between px-3 py-2 border-b border-white/5 last:border-0 text-xs">
                      <span className="text-white/80">{a.name}</span>
                      <div className="flex items-center gap-4">
                        <span className="font-bold tabular-nums" style={{ color: a.color, ...DISPLAY }}>{a.score}</span>
                        <span className="text-white/40 w-12 text-right tabular-nums" style={MONO}>{a.trend}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right : Providence chat preview */}
              <div className="lg:col-span-2 rounded-lg border border-[#A855F7]/20 bg-gradient-to-br from-[#1a0f2e]/60 to-[#0f0a1e]/40 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#A855F7] to-[#681793] flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-white font-medium">Providence</div>
                    <div className="text-[9px] text-[#C084FC]" style={MONO}>IA STRATÉGIQUE</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="px-3 py-2 rounded-md bg-white/5 text-[11px] text-white/70 border border-white/5">
                    <span className="text-white/40">Vous · </span>Quelles agences nécessitent mon attention ?
                  </div>
                  <div className="px-3 py-2.5 rounded-md bg-[#A855F7]/10 border border-[#A855F7]/20 text-[11px] text-white/85 leading-relaxed">
                    <span className="text-[#C084FC] font-medium">Orange</span> cumule baisse satisfaction (3,6/5) et process à 62%.{' '}
                    <span className="text-white">Un point avec le directeur est recommandé sous 7 jours.</span>{' '}
                    <span className="text-[#10b981]">Avignon</span> peut servir de modèle (91/100).
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Halo bottom */}
          <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-[80%] h-32 bg-[#A855F7]/15 blur-[80px] rounded-full pointer-events-none" />
        </motion.div>

        {/* 4 valeurs clés */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-5"
        >
          {[
            { icon: Eye, title: 'Vue groupe en 30 sec', sub: "Score, tendances, agences à risque" },
            { icon: GitCompareArrows, title: 'Comparatif inter-agences', sub: 'Process, satisfaction, adoption' },
            { icon: Crown, title: 'Brief directeur prêt-à-coller', sub: 'Providence prépare votre appel' },
            { icon: Layers, title: 'PDF & CSV exportables', sub: 'Pour vos COPIL / board' },
          ].map((v, i) => {
            const Icon = v.icon;
            return (
              <div key={i} className="group" data-testid={`providence-value-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#A855F7]/20 to-[#FF914D]/10 border border-[#A855F7]/20 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                  <Icon className="w-5 h-5 text-[#C084FC]" />
                </div>
                <div className="text-sm font-semibold text-white mb-1" style={DISPLAY}>{v.title}</div>
                <div className="text-xs text-white/50 leading-relaxed">{v.sub}</div>
              </div>
            );
          })}
        </motion.div>

        {/* Différentiateur clé */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 max-w-3xl mx-auto text-center"
        >
          <div className="inline-block px-5 py-3 rounded-lg border border-white/10 bg-white/[0.02]">
            <span className="text-[10px] uppercase tracking-widest text-[#C084FC]" style={MONO}>La promesse Providence</span>
            <p className="mt-2 text-base sm:text-lg text-white/85 italic leading-relaxed" style={DISPLAY}>
              «&nbsp;Je ne regarde plus chaque relance à la place de mes directeurs. Je vois où agir,
              avec qui échanger, et quelles agences accompagner.&nbsp;»
            </p>
            <div className="mt-3 text-xs text-white/40" style={MONO}>— Client Groupe Homly · réseau multi-agence</div>
          </div>
        </motion.div>

        {/* CTA Final */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            onClick={onDemoClick}
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-[#A855F7] to-[#FF914D] text-white font-semibold shadow-xl shadow-[#A855F7]/30 hover:shadow-[#A855F7]/50 hover:scale-[1.02] transition-all"
            data-testid="providence-cta-demo"
          >
            <Network className="w-5 h-5" />
            Voir Providence en démo
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <div className="text-xs text-white/40" style={MONO}>
            ~ 25 min · Démo personnalisée sur votre réseau
          </div>
        </motion.div>
      </div>
    </section>
  );
};
